/**
 * Small utility glyphs for the providers surface (metrics cells, row actions, version rows),
 * following the app's inline-<svg> convention (no icon library - see RepoProviderIcon.tsx).
 * All stroke-based, `currentColor`, `aria-hidden` - the accessible name lives on the wrapping
 * button/link (aria-label or Tooltip), never on the glyph.
 */

import type { CSSProperties } from 'react'

function common(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
    style: { verticalAlign: 'text-bottom', flexShrink: 0 } as CSSProperties,
  }
}

/** Star. Outline for a repo's star count; `filled` for a curated/favourite marker. */
export function StarIcon({ size = 13, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg {...common(size)} {...(filled ? { fill: 'currentColor' } : {})}>
      <path d="M12 2.5l2.94 5.95 6.56.95-4.75 4.63 1.12 6.54L12 17.48l-5.87 3.09 1.12-6.54L2.5 9.4l6.56-.95L12 2.5z" />
    </svg>
  )
}

/** Commit (branch dot on a line). */
export function CommitIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <circle cx="12" cy="12" r="3.5" />
      <path d="M2 12h6.5M15.5 12H22" />
    </svg>
  )
}

/** Tag (release). */
export function TagIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M20.6 13.4L13.4 20.6a2 2 0 0 1-2.83 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
      <circle cx="7.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Download arrow into a tray. */
export function DownloadIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M12 3v11M7.5 10.5L12 15l4.5-4.5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}

/** Code angle-brackets (consume config). */
export function CodeIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M8 6l-6 6 6 6M16 6l6 6-6 6" />
    </svg>
  )
}

/** Shield (policies). */
export function ShieldIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M12 2.5l8 3.5v5c0 5-3.4 8.6-8 10.5C7.4 19.6 4 16 4 11V6l8-3.5z" />
    </svg>
  )
}

/** Document with a down arrow (SBOM download). */
export function FileDownIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
      <path d="M14 3v5h5" />
      <path d="M12 11v6M9.5 14.5L12 17l2.5-2.5" />
    </svg>
  )
}

/** External-link (arrow leaving a box). */
export function ExternalLinkIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M10 14L21 3" />
    </svg>
  )
}

/** Circular arrows (re-scan / refresh). */
export function RescanIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v5h-5" />
    </svg>
  )
}

/** Pencil (edit-config affordance). */
export function EditIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

/** Trash (delete/disassociate affordance). */
export function TrashIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  )
}

/** Unlink (unmanage affordance): a broken chain, deliberately not a trash can - nothing is deleted. */
export function UnlinkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M2 2l20 20" />
    </svg>
  )
}

/** Plus (create/manage affordance). */
export function PlusIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/** Gear (configuration affordance). */
export function SettingsIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

/** Open padlock (force unlock). */
export function UnlockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.7-1.5" />
    </svg>
  )
}

/** Closed padlock (locked state). */
export function LockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

/** Alert triangle (warning state, e.g. secrets detected). */
export function AlertIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

/** Two overlapping rects (copy to clipboard). */
export function CopyIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

/** Checkmark (success/copied state). */
export function CheckIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

/** Counter-clockwise clock arrow (restore/promote/undeprecate). */
export function RestoreIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M3 12a9 9 0 1 0 2.64-6.36" />
      <path d="M3 3v6h6" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  )
}

/** Two opposing semicircle arrows (client-side refresh - distinct from RescanIcon's
 *  single loop, which means "queue a server-side scan"). */
export function RefreshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  )
}

/** Cube (resource count). */
export function BoxIcon({ size = 13 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.3 7L12 12l8.7-5M12 22V12" />
    </svg>
  )
}

/** Box with lid (archive/deprecate). */
export function ArchiveIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <rect x="2" y="4" width="20" height="4" rx="1" />
      <path d="M4 8v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </svg>
  )
}

/**
 * Integrated circuit. A provider is a compiled Go binary Terraform downloads and executes, so
 * a chip ("a compiled component that slots into a host") says what the artefact IS. A shield
 * would say *trusted*, which is a property Terramantle adds rather than the thing itself - and
 * shield is already spoken for by policies.
 */
export function ChipIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </svg>
  )
}

/** Stacked cylinder (a stored snapshot of real infrastructure). Shares the sidebar's State glyph. */
export function DatabaseIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </svg>
  )
}

/** Crosshair (focus this node / isolate its lineage). */
export function CrosshairIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  )
}

/** Struck-through eye (hide this node from the canvas). */
export function EyeOffIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M6.6 6.6A18.2 18.2 0 0 0 2 12s3 8 10 8a9.3 9.3 0 0 0 5.4-1.6" />
      <path d="M2 2l20 20" />
    </svg>
  )
}

/** Arrow out of a box (upload a lock file). */
export function UploadIcon({ size = 14 }: { size?: number }) {
  return (
    <svg {...common(size)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5M12 3v12" />
    </svg>
  )
}
